/**
 * InflammatoryClinicalEnginePage — Research Preview за Inflammatory IR модула.
 *
 * ⚠️ BETA / RESEARCH PREVIEW — модулът НЕ Е FROZEN.
 * Peer Review V5 (Feb 2026) — 7/7 concerns resolved.
 * Валидирано покритие: 2 от 10 правила (I-01-HSCRP, I-09-NLR) в NHANES 2017-2018.
 *
 * Тази страница е read-only визуализация на V5 statistical findings.
 * Няма live API — Etap 6 API integration чака cross-dataset replication
 * (MESA / Framingham / UK Biobank).
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, FlaskConical, AlertTriangle, ShieldCheck, Info,
  TrendingUp, ChartBar, BookOpen,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────
// V5 findings — hardcoded from NHANES 2017-2018 (n=1,740)
// Source: /app/backend/nhanes_etl/v5_statistical_appendix.py
// ─────────────────────────────────────────────────────────────
const CONSTRUCT_VALIDITY = [
  { predictor: "Waist",   r: +0.417, lo: +0.374, hi: +0.459, cls: "Convergent",   delta: null },
  { predictor: "BMI",     r: +0.429, lo: +0.387, hi: +0.469, cls: "Convergent",   delta: null },
  { predictor: "WBC",     r: +0.270, lo: +0.223, hi: +0.316, cls: "Convergent",   delta: null },
  { predictor: "TG/HDL",  r: +0.069, lo: -0.004, hi: +0.142, cls: "Divergent",    delta: "UNDET → Div" },
  { predictor: "HbA1c",   r: +0.160, lo: +0.111, hi: +0.208, cls: "Undetermined", delta: "Div → UNDET" },
];

const VIF_TABLE = [
  { cov: "age",         vif: 1.64 },
  { cov: "hba1c",       vif: 1.27 },
  { cov: "statin",      vif: 1.58 },
  { cov: "bmi",         vif: 5.92 },
  { cov: "waist",       vif: 6.22 },
  { cov: "total_chol",  vif: 8.97 },
  { cov: "ldl",         vif: 9.09 },
];

const V5_CONCERNS = [
  { id: "C1",  concern: "Premature freeze",                  priority: "Critical",  status: "Resolved" },
  { id: "C2",  concern: "Divergent validity CI",             priority: "Critical",  status: "Resolved" },
  { id: "M1",  concern: "Multicollinearity",                 priority: "Major",     status: "Resolved" },
  { id: "M2",  concern: "LDL pleiotropy interpretation",     priority: "Major",     status: "Resolved" },
  { id: "Mi1", concern: "NLR retention justification",       priority: "Minor",     status: "Resolved" },
  { id: "E1",  concern: "C3/M11 heading typo",               priority: "Editorial", status: "Resolved" },
  { id: "E2",  concern: "TG/HDL classification",             priority: "Editorial", status: "Resolved" },
];

const clsStyle = {
  Convergent:   "bg-emerald-100 text-emerald-800 border-emerald-300",
  Divergent:    "bg-slate-100 text-slate-700 border-slate-300",
  Undetermined: "bg-amber-100 text-amber-800 border-amber-300",
};

const priorityStyle = {
  Critical:  "bg-rose-100 text-rose-700 border-rose-300",
  Major:     "bg-amber-100 text-amber-800 border-amber-300",
  Minor:     "bg-slate-100 text-slate-700 border-slate-300",
  Editorial: "bg-slate-100 text-slate-500 border-slate-300",
};

const vifColor = (v) =>
  v < 2.5 ? "bg-emerald-500" :
  v < 5.0 ? "bg-amber-500" :
  v < 10.0 ? "bg-rose-500" : "bg-rose-900";

// ─────────────────────────────────────────────────────────────
// ForestPlot — small SVG error-bar visualization for construct validity
// ─────────────────────────────────────────────────────────────
const ForestPlot = ({ data }) => {
  const width = 380;
  const rowH = 42;
  const height = data.length * rowH + 40;
  const padL = 90;
  const padR = 20;
  const xMin = -0.10;
  const xMax = 0.55;
  const scale = (v) => padL + ((v - xMin) / (xMax - xMin)) * (width - padL - padR);
  const clsColor = { Convergent: "#10b981", Divergent: "#94a3b8", Undetermined: "#f59e0b" };

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" data-testid="forest-plot">
      {/* x-axis */}
      <line x1={padL} x2={width - padR} y1={height - 20} y2={height - 20} stroke="#cbd5e1" strokeWidth="1" />
      {[-0.1, 0, 0.1, 0.2, 0.3, 0.4, 0.5].map((tick) => (
        <g key={tick}>
          <line x1={scale(tick)} x2={scale(tick)} y1={height - 20} y2={height - 16} stroke="#94a3b8" />
          <text x={scale(tick)} y={height - 4} fontSize="10" fill="#64748b" textAnchor="middle">
            {tick.toFixed(1)}
          </text>
        </g>
      ))}
      {/* r=0.20 threshold */}
      <line
        x1={scale(0.20)} x2={scale(0.20)}
        y1={10} y2={height - 20}
        stroke="#f59e0b" strokeDasharray="4 4" strokeWidth="1.5"
      />
      <text x={scale(0.20) + 4} y={16} fontSize="9" fill="#f59e0b">r = 0.20</text>
      {/* zero line */}
      <line x1={scale(0)} x2={scale(0)} y1={10} y2={height - 20} stroke="#cbd5e1" strokeWidth="1" />
      {/* rows */}
      {data.map((d, i) => {
        const y = 24 + i * rowH;
        const color = clsColor[d.cls] || "#94a3b8";
        return (
          <g key={d.predictor}>
            <text x={padL - 8} y={y + 5} fontSize="12" fill="#334155" textAnchor="end" fontWeight="500">
              {d.predictor}
            </text>
            <line x1={scale(d.lo)} x2={scale(d.hi)} y1={y} y2={y} stroke={color} strokeWidth="2" />
            <line x1={scale(d.lo)} x2={scale(d.lo)} y1={y - 5} y2={y + 5} stroke={color} strokeWidth="2" />
            <line x1={scale(d.hi)} x2={scale(d.hi)} y1={y - 5} y2={y + 5} stroke={color} strokeWidth="2" />
            <circle cx={scale(d.r)} cy={y} r={5} fill={color} stroke="white" strokeWidth="1.5" />
          </g>
        );
      })}
    </svg>
  );
};

// ─────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────
export default function InflammatoryClinicalEnginePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="inflammatory-clinical-engine-page">
      {/* ─── Header ─── */}
      <div className="bg-gradient-to-r from-rose-900 via-rose-800 to-orange-800 text-white">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-white hover:bg-white/10 mb-4 -ml-3"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Обратно към Dashboard
          </Button>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <Badge className="bg-amber-500/90 text-white border-0 uppercase tracking-wider text-[10px]" data-testid="beta-badge">
              <FlaskConical className="w-3 h-3 mr-1" /> Beta · Research preview
            </Badge>
            <Badge className="bg-white/15 text-white border border-white/30 uppercase tracking-wider text-[10px]">
              Partial proof-of-concept
            </Badge>
            <Badge className="bg-white/15 text-white border border-white/30 uppercase tracking-wider text-[10px]">
              Peer Review V5 · 7/7 resolved
            </Badge>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
            Inflammatory Clinical Engine
          </h1>
          <p className="text-white/80 text-sm md:text-base">
            Инфламаторна инсулинова резистентност · NHANES 2017-2018 (n = 1,740) ·
            Валидирано покритие 2/10 правила · Freeze certificate НЕ е издаден
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">

        {/* ─── Beta banner ─── */}
        <Card className="border-amber-300 bg-amber-50/60" data-testid="beta-banner">
          <CardContent className="p-4 flex gap-3 items-start">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <b>Research preview — не за клинично приложение.</b> Този модул
              представя partial empirical proof-of-concept за 2 от 10 инфламаторни
              правила, валидирани в NHANES 2017-2018 J-cycle. Пълна валидация е
              условена от cross-dataset replication (MESA / Framingham Offspring
              / UK Biobank inflammation subset).
            </div>
          </CardContent>
        </Card>

        {/* ─── Key metrics ─── */}
        <Card data-testid="key-metrics-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <TrendingUp className="w-5 h-5 text-rose-600" />
              Key treatment-effect metrics (V5 canonical)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricBox label="Cohort (NHANES J)" value="1,740" sub="възрастни ≥ 20 г." testid="metric-cohort" />
              <MetricBox label="Matched pairs" value="159" sub="propensity caliper 0.05" testid="metric-pairs" />
              <MetricBox label="Wilcoxon p (paired)" value="0.0026" sub="signed-rank" tone="ok" testid="metric-wilcoxon" />
              <MetricBox label="Median Δ hsCRP" value="−0.49 mg/L" sub="95% CI (−1.20, −0.03)" tone="ok" testid="metric-delta" />
            </div>
            <div className="mt-4 text-xs text-slate-500 flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>
                Primary treatment-effect estimate се базира на propensity-matched
                paired Wilcoxon signed-rank test. Мултивариабилната регресия е
                премахната поради severe multicollinearity (V5 M1; max VIF = 9.09).
              </span>
            </div>
          </CardContent>
        </Card>

        {/* ─── Construct validity ─── */}
        <Card data-testid="construct-validity-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <ChartBar className="w-5 h-5 text-rose-600" />
              Construct validity под V5 правилата (C2 · E2)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6 items-start">
              <div className="p-4 border border-slate-200 rounded-lg bg-slate-50/50">
                <ForestPlot data={CONSTRUCT_VALIDITY} />
                <div className="mt-2 text-xs text-slate-500 text-center">
                  Pearson r с 95% CI · оранжевата линия = r = 0.20 threshold
                </div>
              </div>
              <div>
                <table className="w-full text-sm" data-testid="construct-table">
                  <thead>
                    <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                      <th className="text-left py-2 font-medium">Predictor</th>
                      <th className="text-right py-2 font-medium">r (95% CI)</th>
                      <th className="text-right py-2 font-medium">Class (V5)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CONSTRUCT_VALIDITY.map((d) => (
                      <tr key={d.predictor} className="border-b border-slate-100" data-testid={`row-${d.predictor.toLowerCase().replace("/", "-")}`}>
                        <td className="py-2 font-medium">{d.predictor}</td>
                        <td className="py-2 text-right font-mono text-xs">
                          {d.r.toFixed(3)} <span className="text-slate-400">({d.lo.toFixed(3)}, {d.hi.toFixed(3)})</span>
                        </td>
                        <td className="py-2 text-right">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${clsStyle[d.cls]}`}>
                            {d.cls}
                          </span>
                          {d.delta && (
                            <div className="text-[10px] text-amber-600 mt-0.5">{d.delta}</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-xs text-slate-500 mt-3">
                  <b>V5 C2 правило</b>: Divergent изисква upper 95% CI &lt; 0.20 (не само point estimate).
                  HbA1c (upper CI = 0.208) → Undetermined. TG/HDL (upper CI = 0.142) → Divergent (E2 fix).
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ─── VIF ─── */}
        <Card data-testid="vif-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <ShieldCheck className="w-5 h-5 text-rose-600" />
              Multicollinearity — VIF audit (M1)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="vif-bars">
              {VIF_TABLE.sort((a, b) => a.vif - b.vif).map((row) => {
                const pct = Math.min(100, (row.vif / 12) * 100);
                return (
                  <div key={row.cov} className="flex items-center gap-3" data-testid={`vif-${row.cov}`}>
                    <div className="w-28 text-sm font-medium text-slate-700">{row.cov}</div>
                    <div className="flex-1 bg-slate-100 rounded h-6 relative overflow-hidden">
                      <div
                        className={`h-full ${vifColor(row.vif)} transition-all`}
                        style={{ width: `${pct}%` }}
                      />
                      <div className="absolute inset-0 flex items-center justify-between px-2">
                        <span className="text-xs font-mono text-slate-700">{row.vif.toFixed(2)}</span>
                        <span className="text-xs text-slate-500">
                          {row.vif < 2.5 ? "OK" : row.vif < 5 ? "Moderate" : row.vif < 10 ? "High" : "Severe"}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 p-3 bg-rose-50 border-l-4 border-rose-400 rounded text-sm text-rose-900">
              <b>Verdict (V5 M1)</b>: Max VIF = <b>9.09</b> (LDL) → severe multicollinearity
              между LDL, total cholesterol, waist, BMI. Мултивариабилната регресия е <b>изтеглена </b>
              от primary analysis. Paired Wilcoxon signed-rank остава canonical treatment-effect test.
            </div>
          </CardContent>
        </Card>

        {/* ─── NLR safety-gate yield ─── */}
        <Card data-testid="safety-gate-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <ShieldCheck className="w-5 h-5 text-rose-600" />
              NLR safety-gate yield (Mi1) — data-driven justification
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <SafetyGateBox
                title="I-FLAG-01 hsCRP-only"
                count={1}
                pct="0.06%"
                desc="hsCRP > 100 mg/L (sepsis range)"
                tone="warn"
                testid="flag01-hscrp"
              />
              <SafetyGateBox
                title="I-FLAG-01 NLR-only"
                count={11}
                pct="0.71%"
                desc="NLR > 7, hsCRP ≤ 100 — missed by hsCRP alone"
                tone="ok"
                testid="flag01-nlr"
              />
              <SafetyGateBox
                title="I-FLAG-02 (NLR required)"
                count={39}
                pct="2.52%"
                desc="hsCRP 10-100 AND NLR 3-7 (subclinical acute)"
                tone="info"
                testid="flag02"
              />
            </div>
            <div className="mt-4 p-3 bg-emerald-50 border-l-4 border-emerald-500 rounded text-sm text-emerald-900">
              <b>Data-driven verdict</b>: Без NLR, <b>39 пациенти</b> (2.52% от NHANES кохортата)
              биха били misclassified като chronic CLGI. NLR не се задържа за predictive
              value (Δr ≈ 0-0.02 vs hsCRP alone), а като orthogonal safety-gate signal.
            </div>
          </CardContent>
        </Card>

        {/* ─── V5 concerns summary ─── */}
        <Card data-testid="v5-concerns-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <BookOpen className="w-5 h-5 text-rose-600" />
              Peer Review V5 · Concerns status (7/7 resolved)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="concerns-table">
                <thead>
                  <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                    <th className="text-left py-2 font-medium">ID</th>
                    <th className="text-left py-2 font-medium">Concern</th>
                    <th className="text-left py-2 font-medium">Priority</th>
                    <th className="text-left py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {V5_CONCERNS.map((c) => (
                    <tr key={c.id} className="border-b border-slate-100" data-testid={`concern-${c.id}`}>
                      <td className="py-2 font-mono text-xs">{c.id}</td>
                      <td className="py-2">{c.concern}</td>
                      <td className="py-2">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${priorityStyle[c.priority]}`}>
                          {c.priority}
                        </span>
                      </td>
                      <td className="py-2">
                        <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-300">
                          ✓ {c.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* ─── Freeze gate roadmap ─── */}
        <Card className="border-slate-300 bg-slate-50/60" data-testid="freeze-gate-card">
          <CardHeader>
            <CardTitle className="text-lg">Freeze gate roadmap</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-700 mb-3">
              Модулът НЕ може да премине към FROZEN status преди изпълнение на всички
              следните условия:
            </p>
            <ol className="text-sm text-slate-700 space-y-2 list-decimal ml-5">
              <li><b>Cross-dataset replication</b> на treatment-effect (Wilcoxon signed-rank) в поне 1 независим propensity-matched кохорт (MESA препоръчано).</li>
              <li><b>Full-panel construct validation</b> — IL-6, TNF-α, adiponectin, ferritin, fibrinogen, omega-3 измерени в един кохорт (MESA или Framingham Offspring).</li>
              <li><b>Longitudinal mediation analysis</b> за LDL vs pleiotropy distinction (MESA visits 1-5).</li>
              <li><b>Communication-layer utility pilot</b> — physician A/B trial + patient comprehension study.</li>
            </ol>
          </CardContent>
        </Card>

        {/* ─── Docs footer ─── */}
        <div className="text-xs text-slate-500 pt-4 border-t border-slate-200">
          Източник: <code className="bg-slate-100 px-1.5 py-0.5 rounded">/app/backend/nhanes_etl/v5_statistical_appendix.py</code> ·
          Peer Review response: <code className="bg-slate-100 px-1.5 py-0.5 rounded">INFLAMMATORY_PEER_REVIEW_V5_RESPONSE.md</code> ·
          Limitations: <code className="bg-slate-100 px-1.5 py-0.5 rounded">INFLAMMATORY_LIMITATIONS_DECLARATION.md</code>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Reusable components
// ─────────────────────────────────────────────────────────────
const MetricBox = ({ label, value, sub, tone, testid }) => {
  const valueColor = tone === "ok" ? "text-emerald-600" : "text-slate-900";
  return (
    <div className="p-3 border border-slate-200 rounded-lg bg-white" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${valueColor}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
};

const SafetyGateBox = ({ title, count, pct, desc, tone, testid }) => {
  const toneColor = {
    ok:   "border-emerald-300 bg-emerald-50 text-emerald-900",
    warn: "border-amber-300 bg-amber-50 text-amber-900",
    info: "border-sky-300 bg-sky-50 text-sky-900",
  }[tone] || "border-slate-300 bg-slate-50 text-slate-900";
  return (
    <div className={`p-4 rounded-lg border ${toneColor}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-wider font-medium opacity-75">{title}</div>
      <div className="text-3xl font-bold mt-1">{count}</div>
      <div className="text-sm font-medium mt-0.5">{pct}</div>
      <div className="text-xs mt-2 opacity-75">{desc}</div>
    </div>
  );
};
